import { encodeFunctionData } from 'viem';
import { ethers } from 'ethers';

import { abiSpinWheel } from '../config/abi.contract';
import useWrappedRPC from './useWrappedRPC';
import useEmbeddedWallet from './useEmbeddedWallet';
import { tokenSpinSignature, validateTokenWheelTransaction } from '../services/transaction.service';
import useToken from './useToken';
import { dispatchGameEvent } from '../utils/common';
import environments from '../utils/environments';

const { PUBLIC_RPC_URL, SPIN_WHEEL_CONTRACT_ADDRESS } = environments;

function useSpinWheel() {
  const { getXFISHTokenBalance } = useToken();
  const { WrappedJsonProvider, sendTransactionWithGas, customChainNetwork } = useWrappedRPC();
  const { getEmbeddedWallet } = useEmbeddedWallet();

  // Contracts
  const provider = new WrappedJsonProvider(PUBLIC_RPC_URL, customChainNetwork);
  const spinWheelContract = new ethers.Contract(SPIN_WHEEL_CONTRACT_ADDRESS, abiSpinWheel, provider);

  const spin = async () => {
    try {
      const embeddedWallet = await getEmbeddedWallet();
      const vrfFee = await spinWheelContract.vRNGFee();

      const res = await tokenSpinSignature();
      const { user, nonce, deadline, signature } = res.data.data;

      const request = {
        user,
        rngFee: vrfFee,
        nonce,
        deadline,
      };

      // Encode transaction data
      const data = encodeFunctionData({
        abi: abiSpinWheel,
        functionName: 'sendSpinRequest',
        args: [request, signature],
      });

      const txUiConfig = {
        header: 'Spin Token Wheel',
        description: `Spin token wheel`,
        buttonText: 'Spin',
      };

      const transactionRequest = {
        to: SPIN_WHEEL_CONTRACT_ADDRESS,
        value: vrfFee,
        data,
      };

      // Send transaction
      const txReceipt = await sendTransactionWithGas(transactionRequest, txUiConfig);
      dispatchGameEvent({ type: 'confirmStartSpinTokenWheel', hasWaiting: true });

      const wheelType = 'Token';
      const spinResult = await validateTokenWheelTransaction({ wheelType, transactionHash: txReceipt.hash });
      const xFishTokenBalance = await getXFISHTokenBalance(embeddedWallet.address);

      if (!txReceipt?.hash) {
        throw new Error('Transaction failed on blockchain');
      }

      return {
        success: true,
        message: 'Spin successfully',
        data: {
          spinResult: spinResult.data.data,
          xFishTokenBalance,
        },
      };
    } catch (error) {
      console.error('❌ Spin failed:', error);
      return {
        success: false,
        message: error.message || 'Failed to spin',
      };
    }
  };

  return {
    spin,
  };
}

export default useSpinWheel;
